import React, { useMemo, useState } from "react";
import styles from "../../styles/pages/games/LawGame.module.scss";

const QUESTION_BANK = {
  international: {
    title: "International Law",
    blurb: "Know the basics that impact travel, conflict, human rights, and treaties.",
    questions: [
      {
        q: "What is a treaty?",
        choices: [
          "A political speech made by a president",
          "A legally binding agreement between states (countries)",
          "A court’s internal policy memo",
          "A non-binding press statement",
        ],
        answerIndex: 1,
        explain:
          "A treaty is a binding agreement under international law between states (and sometimes international organizations).",
      },
      {
        q: "Which body is the primary judicial organ of the United Nations?",
        choices: ["International Criminal Court (ICC)", "International Court of Justice (ICJ)", "European Court of Human Rights", "WTO Appellate Body"],
        answerIndex: 1,
        explain:
          "The ICJ is the UN’s principal judicial organ, handling disputes between states and advisory opinions.",
      },
      {
        q: "What does “asylum” generally mean in international protection contexts?",
        choices: [
          "A tourist visa for temporary entry",
          "Protection granted to someone fleeing persecution",
          "A work permit for skilled workers only",
          "A program to reduce taxes for immigrants",
        ],
        answerIndex: 1,
        explain:
          "Asylum is protection for people who cannot safely return home due to persecution (often tied to refugee law).",
      },
      {
        q: "Which principle means states should not send a person to a place where they face serious harm?",
        choices: ["Non-refoulement", "Sovereign immunity", "Uti possidetis", "Extradition parity"],
        answerIndex: 0,
        explain:
          "Non-refoulement is a cornerstone of refugee and human rights law: don’t return someone to serious danger.",
      },
    ],
  },

  immigration: {
    title: "Immigration Law",
    blurb: "Practical knowledge: status, due process basics, and common protections.",
    questions: [
      {
        q: "In many systems, what does “due process” generally require in government proceedings?",
        choices: [
          "A social media announcement of the decision",
          "A fair procedure (notice + opportunity to be heard)",
          "A guaranteed win in court",
          "A free lawyer in all cases",
        ],
        answerIndex: 1,
        explain:
          "Due process usually means fair procedures: notice, a chance to respond, and impartial decision-making.",
      },
      {
        q: "What is a common meaning of “visa overstay”?",
        choices: [
          "Entering without inspection at the border",
          "Staying past the authorized period of admission",
          "Applying for citizenship too early",
          "Renewing a passport late",
        ],
        answerIndex: 1,
        explain:
          "Overstay means you remained beyond your authorized time. Consequences vary by country and status.",
      },
      {
        q: "What does “work authorization” typically allow?",
        choices: [
          "Voting in national elections",
          "Legally working for pay under the jurisdiction’s rules",
          "Automatic citizenship",
          "Permanent residency everywhere",
        ],
        answerIndex: 1,
        explain:
          "Work authorization generally allows lawful employment. It does not automatically grant citizenship.",
      },
      {
        q: "What is a “removal/deportation proceeding” (general concept)?",
        choices: [
          "A process to revoke a driver’s license",
          "A process determining whether someone must leave a country",
          "A process for changing your name",
          "A process for getting a student ID",
        ],
        answerIndex: 1,
        explain:
          "Removal/deportation proceedings decide whether a person can stay or must depart, with specific legal rights involved.",
      },
    ],
  },

  urban: {
    title: "Urban Law (Housing, Zoning, Local Government)",
    blurb: "Know tenant basics, city rules, and what zoning means for neighborhoods.",
    questions: [
      {
        q: "What does “zoning” generally regulate?",
        choices: [
          "National defense spending",
          "How land can be used (residential, commercial, industrial, etc.)",
          "The price of groceries",
          "International borders",
        ],
        answerIndex: 1,
        explain:
          "Zoning sets rules for land use, building size, density, and what activities can occur where.",
      },
      {
        q: "In many places, what is a common tenant protection concept?",
        choices: [
          "A landlord can enter anytime without notice",
          "Habitability: basic safe living conditions must be maintained",
          "Tenants must pay for all building repairs",
          "Rent can be changed hourly",
        ],
        answerIndex: 1,
        explain:
          "Habitability rules often require safe, livable conditions (heat, water, structural safety, etc.).",
      },
      {
        q: "What is a “local ordinance”?",
        choices: [
          "A rule passed by a city or local government",
          "A treaty signed by multiple countries",
          "A constitutional amendment",
          "A private company policy",
        ],
        answerIndex: 0,
        explain:
          "Ordinances are local laws passed by cities/counties (noise rules, housing codes, zoning, permits, etc.).",
      },
      {
        q: "What does a building “permit” generally authorize?",
        choices: [
          "Travel between countries",
          "A specific construction/renovation activity under code rules",
          "Automatic home ownership",
          "Exemption from taxes forever",
        ],
        answerIndex: 1,
        explain:
          "Permits are approvals to do certain work legally and safely according to building codes.",
      },
    ],
  },

  constitutional: {
    title: "Constitutional Rights (Basics)",
    blurb: "Core rights and government limits that protect people in everyday life.",
    questions: [
      {
        q: "What is a constitution primarily?",
        choices: [
          "A list of suggested manners",
          "A foundational legal framework defining government powers and rights",
          "A sports rulebook",
          "A private contract between two companies",
        ],
        answerIndex: 1,
        explain:
          "Constitutions set the structure of government and often protect fundamental rights and liberties.",
      },
      {
        q: "What does “freedom of speech” generally protect?",
        choices: [
          "Only speech that the government likes",
          "The ability to express ideas, with some legal limits depending on jurisdiction",
          "Only written speech, not spoken",
          "Only speech from politicians",
        ],
        answerIndex: 1,
        explain:
          "Speech protections vary by country, but generally protect expression while allowing certain limited restrictions.",
      },
      {
        q: "What does “equal protection” (general concept) mean?",
        choices: [
          "The law must treat everyone exactly the same in all situations",
          "Government must not unfairly discriminate; like cases should be treated alike",
          "Only citizens get legal protection",
          "Only businesses get legal protection",
        ],
        answerIndex: 1,
        explain:
          "Equal protection is about preventing unfair discrimination and requiring fair, consistent treatment under law.",
      },
      {
        q: "What is “unreasonable search” generally about?",
        choices: [
          "Police can search anyone anytime",
          "Limits on government searches; often requires legal justification",
          "Only applies to searching on Google",
          "Only applies to airports, always",
        ],
        answerIndex: 1,
        explain:
          "Many constitutions limit government searches and require justification (often warrants or exceptions).",
      },
    ],
  },
};

function shuffle(array) {
  const a = [...array];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function LawGame({ numQuestions = 5 }) {
  const categories = useMemo(
    () =>
      Object.entries(QUESTION_BANK).map(([key, val]) => ({
        key,
        ...val,
      })),
    []
  );

  const [screen, setScreen] = useState("choose"); // choose | quiz | result
  const [categoryKey, setCategoryKey] = useState(null);

  const [quiz, setQuiz] = useState([]); // questions for session
  const [index, setIndex] = useState(0);

  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);

  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState([]); // {correct, selectedIndex}

  const category = categoryKey ? QUESTION_BANK[categoryKey] : null;
  const current = quiz[index];

  const startCategory = (key) => {
    const bank = QUESTION_BANK[key].questions;
    const chosen = shuffle(bank).slice(0, Math.min(numQuestions, bank.length));
    setCategoryKey(key);
    setQuiz(chosen);
    setIndex(0);
    setSelected(null);
    setLocked(false);
    setScore(0);
    setAnswers([]);
    setScreen("quiz");
  };

  const choose = (choiceIndex) => {
    if (locked) return;
    setSelected(choiceIndex);
  };

  const confirm = () => {
    if (locked || selected == null) return;

    const correct = selected === current.answerIndex;
    setLocked(true);
    setScore((s) => s + (correct ? 1 : 0));
    setAnswers((a) => [...a, { correct, selectedIndex: selected }]);
  };

  const next = () => {
    if (!locked) return;

    const isLast = index >= quiz.length - 1;
    if (isLast) {
      setScreen("result");
      return;
    }

    setIndex((i) => i + 1);
    setSelected(null);
    setLocked(false);
  };

  const restart = () => {
    setScreen("choose");
    setCategoryKey(null);
    setQuiz([]);
    setIndex(0);
    setSelected(null);
    setLocked(false);
    setScore(0);
    setAnswers([]);
  };

  return (
    <section className={styles.wrap} aria-label="Law Quiz Game">
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>Law Game</h1>
          <p className={styles.subtitle}>
            Pick a category and learn practical law basics in quick quizzes.
          </p>
        </div>

        <div className={styles.pills} aria-label="Game highlights">
          <span className={styles.pill}>Fast</span>
          <span className={styles.pill}>Practical</span>
          <span className={styles.pill}>Portfolio-ready</span>
        </div>
      </header>

      <main className={styles.card}>
        {screen === "choose" && (
          <>
            <div className={styles.sectionHead}>
              <h2 className={styles.h2}>Choose a category</h2>
              <p className={styles.muted}>
                Each quiz is multiple-choice. You’ll see explanations after you
                answer.
              </p>
            </div>

            <div className={styles.grid}>
              {categories.map((c) => (
                <button
                  key={c.key}
                  className={styles.category}
                  onClick={() => startCategory(c.key)}
                >
                  <div className={styles.categoryTop}>
                    <span className={styles.badge}>{c.title}</span>
                    <span className={styles.small}>
                      {c.questions.length} questions
                    </span>
                  </div>
                  <p className={styles.categoryBlurb}>{c.blurb}</p>
                  <span className={styles.cta}>Start →</span>
                </button>
              ))}
            </div>
          </>
        )}

        {screen === "quiz" && category && current && (
          <>
            <div className={styles.quizTop}>
              <div>
                <div className={styles.kicker}>{category.title}</div>
                <h2 className={styles.h2}>{current.q}</h2>
              </div>

              <div className={styles.stats} aria-label="Progress and score">
                <div className={styles.stat}>
                  <span className={styles.statLabel}>Progress</span>
                  <span className={styles.statValue}>
                    {index + 1}/{quiz.length}
                  </span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statLabel}>Score</span>
                  <span className={styles.statValue}>{score}</span>
                </div>
              </div>
            </div>

            <div className={styles.choices} role="list">
              {current.choices.map((text, i) => {
                const isSelected = selected === i;

                const isCorrect = locked && i === current.answerIndex;
                const isWrong = locked && isSelected && i !== current.answerIndex;

                const cls = [
                  styles.choice,
                  isSelected ? styles.choiceSelected : "",
                  isCorrect ? styles.choiceCorrect : "",
                  isWrong ? styles.choiceWrong : "",
                ].join(" ");

                return (
                  <button
                    key={i}
                    className={cls}
                    onClick={() => choose(i)}
                    role="listitem"
                    aria-pressed={isSelected}
                  >
                    <span className={styles.choiceLetter}>
                      {String.fromCharCode(65 + i)}
                    </span>
                    <span className={styles.choiceText}>{text}</span>
                  </button>
                );
              })}
            </div>

            <div className={styles.actions}>
              <button
                className={styles.secondary}
                onClick={restart}
                type="button"
              >
                Change category
              </button>

              {!locked ? (
                <button
                  className={styles.primary}
                  onClick={confirm}
                  disabled={selected == null}
                  type="button"
                >
                  Check answer
                </button>
              ) : (
                <button
                  className={styles.primary}
                  onClick={next}
                  type="button"
                >
                  {index >= quiz.length - 1 ? "See results" : "Next"}
                </button>
              )}
            </div>

            {locked && (
              <div className={styles.explain} aria-live="polite">
                <div className={styles.explainTitle}>
                  {selected === current.answerIndex ? "Correct ✅" : "Not quite ❗"}
                </div>
                <p className={styles.explainText}>{current.explain}</p>
              </div>
            )}
          </>
        )}

        {screen === "result" && category && (
          <>
            <div className={styles.sectionHead}>
              <h2 className={styles.h2}>Results</h2>
              <p className={styles.muted}>
                Category: <strong>{category.title}</strong>
              </p>
            </div>

            <div className={styles.resultRow}>
              <div className={styles.resultScore}>
                <div className={styles.bigScore}>{score}</div>
                <div className={styles.bigMeta}>
                  out of {quiz.length} correct
                </div>
              </div>

              <div className={styles.resultDetails}>
                <div className={styles.miniGrid}>
                  <div className={styles.mini}>
                    <div className={styles.miniLabel}>Accuracy</div>
                    <div className={styles.miniValue}>
                      {Math.round((score / quiz.length) * 100)}%
                    </div>
                  </div>
                  <div className={styles.mini}>
                    <div className={styles.miniLabel}>Questions</div>
                    <div className={styles.miniValue}>{quiz.length}</div>
                  </div>
                </div>

                <div className={styles.actions}>
                  <button className={styles.secondary} onClick={restart}>
                    Pick another category
                  </button>
                  <button
                    className={styles.primary}
                    onClick={() => startCategory(categoryKey)}
                  >
                    Retry same category
                  </button>
                </div>
              </div>
            </div>

            <div className={styles.review}>
              <h3 className={styles.h3}>Quick review</h3>
              <ol className={styles.reviewList}>
                {quiz.map((qq, i) => {
                  const a = answers[i];
                  const correct = a?.correct;
                  return (
                    <li key={i} className={styles.reviewItem}>
                      <div className={styles.reviewQ}>
                        <span className={styles.reviewIcon}>
                          {correct ? "✅" : "❗"}
                        </span>
                        <span>{qq.q}</span>
                      </div>
                      <div className={styles.reviewA}>
                        Correct:{" "}
                        <strong>{qq.choices[qq.answerIndex]}</strong>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>

            <p className={styles.disclaimer}>
              Educational overview only — not legal advice.
            </p>
          </>
        )}
      </main>
    </section>
  );
}
